import { isPublishMode } from 'store/settings/selectors';
import { shouldSaveCrossDevice } from 'store/user/selectors';
import { STATUS_OK } from 'constants/progressStorage';
import StorageInterface from './storageInterface';
import LocalStorage from './localStorage';
import ExternalStorage from './externalStorage';
import idMapper from './idMapper';

const defaultProgress = {
  url: null,
  user: null,
  answers: [],
  finished: false
};

class ProgressStorage extends StorageInterface {
  constructor() {
    super();
    this.progress = defaultProgress;
    this.storage = null;
    this.useShortIds = false;
    this.questionShortIds = null;
  }

  initialize({ state, useShortIds }) {
    this.useShortIds = useShortIds;
    idMapper.initialize(state, { useShortIds });
    if (shouldSaveCrossDevice(state) && isPublishMode(state)) {
      this.storage = new ExternalStorage(state);
      return;
    }

    this.storage = new LocalStorage(state);
  }

  set user(user) {
    this.progress.user = user;
  }

  set url(url) {
    this.progress.url = url;
  }

  set attemptId(attemptId) {
    this.progress.attemptId = attemptId;
  }

  async restoreProgress() {
    let progress = await this.storage.getProgress();
    if (progress && progress.user && progress.user.email === this.progress.user.email) {
      this.progress = progress;
      return true;
    }

    return false;
  }

  updateQuestionProgress(questionId, response) {
    this.progress.answers.push({
      id: idMapper.map(questionId),
      response
    });
  }

  removeQuestionProgress(questionId) {
    this.progress.answers = this.progress.answers.filter(answer => answer.id !== questionId);
  }

  async saveProgress() {
    if (this.storage && this.storage.saveProgress) {
      await this.storage.saveProgress(this.progress);
    }
  }

  async removeProgress() {
    await this.storage.removeProgress();
    this._clearProgress();
  }

  async authorizeUser(userData) {
    if (this.storage && this.storage.authorizeUser) {
      return this.storage.authorizeUser(userData);
    }

    return null;
  }

  async registerUser(userData) {
    if (this.storage && this.storage.registerUser) {
      return this.storage.registerUser(userData);
    }

    return null;
  }

  async resetPassword(email) {
    if (this.storage && this.storage.resetPassword) {
      return this.storage.resetPassword(email);
    }

    return STATUS_OK;
  }

  logout() {
    if (this.storage && this.storage.logout) {
      this.storage.logout();
    }

    this._clearProgress();
  }

  async sendSecretLink(secretLinkData) {
    if (this.storage && this.storage.sendSecretLink) {
      return this.storage.sendSecretLink(secretLinkData);
    }

    return STATUS_OK;
  }

  use(storage) {
    this.storage = storage;
  }

  get url() {
    return this.progress.url;
  }

  get attemptId() {
    return this.progress.attemptId;
  }

  get answers() {
    if (!this.useShortIds) {
      return this.progress.answers;
    }
    // ToDo complete short id map
    return this.progress.answers.map(({ id, response }) => ({
      id: idMapper.unMap(id),
      response
    }));
  }

  async identifyUser() {
    if (this.storage && this.storage.identifyUser) {
      return this.storage.identifyUser();
    }

    return null;
  }

  async userExists(email) {
    if (this.storage && this.storage.userExists) {
      return this.storage.userExists(email);
    }

    return STATUS_OK;
  }

  getSocialNetworkAuthLink(socialNetwork) {
    if (this.storage && this.storage.getSocialNetworkAuthLink) {
      return this.storage.getSocialNetworkAuthLink(socialNetwork);
    }

    return '#';
  }

  isAuthenticated() {
    if (this.storage && this.storage.isAuthenticated) {
      return this.storage.isAuthenticated();
    }

    return null;
  }

  getHeaders(contentTypes) {
    if (this.storage && this.storage.getHeaders) {
      return this.storage.getHeaders(contentTypes);
    }
    return {};
  }

  _clearProgress() {
    this.progress = defaultProgress;
  }
}

export default new ProgressStorage();
